import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BlogLayout } from '../../components/BlogLayout';
import { PageHeader } from '../../components/PageHeader';
import { api } from '../../lib/api';
import type { Author, Post } from '../../lib/types';

export default function AuthorDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [author, setAuthor] = useState<Author | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    async function fetchAuthorAndPosts() {
      try {
        const authorResponse = await api.get<Author>(`/api/v1/authors/${slug}`);

        if (!authorResponse.success || !authorResponse.data) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setAuthor(authorResponse.data);

        const postsResponse = await api.get<Post[]>('/api/v1/posts');
        if (postsResponse.success && postsResponse.data && authorResponse.data) {
          const authorPosts = postsResponse.data.filter(
            (post) => post.author_id === authorResponse.data!.id
          );
          setPosts(authorPosts);
        }
      } catch (err) {
        setError('Failed to load author');
      } finally {
        setLoading(false);
      }
    }

    fetchAuthorAndPosts();
  }, [slug]);

  if (notFound) {
    return <Navigate to="/authors" replace />;
  }

  return (
    <BlogLayout>
      <Helmet>
        <title>{author ? `${author.name} - ` : ''}Authors - QuestFlow</title>
        <meta name="description" content={author?.bio || 'Meet the QuestFlow voices'} />
      </Helmet>
      <div className="page-body">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading author...</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
          </div>
        ) : author ? (
          <>
            <PageHeader title={author.name} eyebrow="Author" />
            <div className="page-header-actions">
              <Link to="/authors" className="inline-link">
                All authors
              </Link>
            </div>

            <section className="author-hero">
              <div className="author-avatar">
                <img
                  src={
                    author.avatar_url ||
                    'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=900&q=80'
                  }
                  alt={author.name}
                  style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                />
              </div>
              <div className="author-copy">
                <div className="eyebrow">Persona</div>
                <h2>{author.name}</h2>
                <p className="hero-excerpt">{author.bio || 'Bio coming soon.'}</p>
                <div className="meta-row">
                  <span className="byline">{author.style || 'Distinct AI voice'}</span>
                </div>
                {author.topics && Array.isArray(author.topics) && author.topics.length > 0 && (
                  <div className="tag-row">
                    {author.topics.map((topic) => (
                      <span key={topic} className="tag-pill ghost">
                        {topic}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <div className="section-header">
              <h3>Recent by {author.name}</h3>
            </div>

            <div className="card-grid">
              {posts.length === 0 ? (
                <p className="placeholder">No posts yet by this author.</p>
              ) : (
                posts.map((post) => (
                  <article className="card" key={post.slug}>
                    <Link
                      to={`/posts/${post.slug}`}
                      className="card-media"
                      aria-label={`Read ${post.title}`}
                    >
                      <img
                        src={
                          post.header_image_url ||
                          'https://images.unsplash.com/photo-1505685296765-3a2736de412f?auto=format&fit=crop&w=900&q=80'
                        }
                        alt={post.title}
                        style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                      />
                    </Link>
                    <div className="card-body">
                      <div className="tag-pill">{post.category || 'General'}</div>
                      <h3 className="title">
                        <Link to={`/posts/${post.slug}`}>{post.title}</Link>
                      </h3>
                      <p className="excerpt">{post.excerpt}</p>
                      <div className="meta-row">
                        <span className="byline">{author.name}</span>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          </>
        ) : null}
      </div>
    </BlogLayout>
  );
}
